import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Building2, MessageSquare, Search, Star, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

export default function OwnerLandingPage() {
  const { user, isBarOwner } = useAuth();
  const [leadForm, setLeadForm] = useState({ full_name: '', email: '' });
  const [submittingLead, setSubmittingLead] = useState(false);

  const primaryCtaPath = useMemo(() => {
    if (user && isBarOwner) return '/owner/browse-bartenders';
    return '/register';
  }, [user, isBarOwner]);

  const primaryCtaLabel = useMemo(() => {
    if (user && isBarOwner) return 'Open Owner Bartender Marketplace';
    return 'Create Free Owner Account';
  }, [user, isBarOwner]);

  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'For Bar Owners — Hire Bartenders Faster | BarsConnect',
      description:
        'Browse bartender profiles, save favorites, and message candidates directly. BarsConnect helps bar owners hire faster in free beta.',
      keywords:
        'bar owner hiring, hire bartenders, browse bartender profiles, bartender marketplace for owners',
      canonicalUrl: 'https://barsconnect.com/for-bar-owners',
      robots: 'index,follow',
      ogTitle: 'For Bar Owners — Hire Bartenders Faster | BarsConnect',
      ogDescription:
        'Use owner tools to search, shortlist, and message bartenders directly through BarsConnect.',
      twitterTitle: 'For Bar Owners — Hire Bartenders Faster | BarsConnect',
      twitterDescription:
        'Search and connect with bartenders quickly. Free beta access for bar owners.',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'For Bar Owners', item: 'https://barsconnect.com/for-bar-owners' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'BarsConnect for Bar Owners',
        url: 'https://barsconnect.com/for-bar-owners',
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  const handleLeadChange = (field, value) => {
    setLeadForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLeadSubmit = async (event) => {
    event.preventDefault();

    const fullName = leadForm.full_name.trim();
    const email = leadForm.email.trim().toLowerCase();

    if (fullName.length < 2) {
      toast.error('Please enter your full name.');
      return;
    }
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setSubmittingLead(true);
    try {
      await axios.post('/owner-leads', {
        full_name: fullName,
        email,
        source: 'owner_landing_page',
      });
      toast.success('Thanks! We will keep you updated on owner tools.');
      setLeadForm({ full_name: '', email: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to save your info right now.');
    } finally {
      setSubmittingLead(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="owner-landing-page">
      <div className="max-w-6xl mx-auto">
        <section className="mb-12" data-testid="owner-landing-hero-section">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20" data-testid="owner-landing-beta-badge">
            Free Beta for Bar Owners
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="owner-landing-title">
            Hire Bartenders Faster Without Hiring Chaos
          </h1>
          <p className="text-muted-foreground max-w-3xl mb-6" data-testid="owner-landing-subtitle">
            BarsConnect gives owners a focused workflow: search bartender profiles, save favorites, and message candidates directly from one place.
          </p>
          <div className="flex flex-wrap gap-3" data-testid="owner-landing-hero-ctas">
            <Button asChild data-testid="owner-landing-primary-cta-btn">
              <Link to={primaryCtaPath}>{primaryCtaLabel}</Link>
            </Button>
            <Button asChild variant="outline" data-testid="owner-landing-login-cta-btn">
              <Link to="/login">Owner Login</Link>
            </Button>
            <Button asChild variant="outline" data-testid="owner-landing-browse-cta-btn">
              <Link to="/bartenders">Preview Public Bartender Directory</Link>
            </Button>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-testid="owner-landing-value-grid">
          <Card className="bg-white/5 border border-white/10" data-testid="owner-landing-value-card-search">
            <CardContent className="p-5">
              <Search className="h-5 w-5 text-primary mb-3" />
              <h2 className="text-lg font-semibold text-white mb-2">Search Smarter</h2>
              <p className="text-sm text-muted-foreground">Filter bartender profiles by location, availability, and skills to shortlist faster.</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border border-white/10" data-testid="owner-landing-value-card-favorites">
            <CardContent className="p-5">
              <Star className="h-5 w-5 text-primary mb-3" />
              <h2 className="text-lg font-semibold text-white mb-2">Build Your Shortlist</h2>
              <p className="text-sm text-muted-foreground">Save top bartenders to favorites and keep your hiring pipeline organized.</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border border-white/10" data-testid="owner-landing-value-card-messaging">
            <CardContent className="p-5">
              <MessageSquare className="h-5 w-5 text-primary mb-3" />
              <h2 className="text-lg font-semibold text-white mb-2">Message Directly</h2>
              <p className="text-sm text-muted-foreground">Contact bartenders right away when shifts open up and hiring urgency is high.</p>
            </CardContent>
          </Card>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="owner-landing-conversion-section">
          <Card className="bg-white/5 border border-white/10" data-testid="owner-lead-capture-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-semibold text-white" data-testid="owner-lead-capture-title">Get Owner Product Updates</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4" data-testid="owner-lead-capture-copy">
                Leave your name and email to get rollout updates for owner hiring tools.
              </p>
              <form onSubmit={handleLeadSubmit} className="space-y-3" data-testid="owner-lead-capture-form">
                <div>
                  <label htmlFor="owner-lead-name" className="text-xs text-muted-foreground">Full Name</label>
                  <Input
                    id="owner-lead-name"
                    value={leadForm.full_name}
                    onChange={(event) => handleLeadChange('full_name', event.target.value)}
                    placeholder="Your full name"
                    data-testid="owner-lead-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="owner-lead-email" className="text-xs text-muted-foreground">Work Email</label>
                  <Input
                    id="owner-lead-email"
                    type="email"
                    value={leadForm.email}
                    onChange={(event) => handleLeadChange('email', event.target.value)}
                    placeholder="you@bar.com"
                    data-testid="owner-lead-email-input"
                  />
                </div>
                <Button type="submit" disabled={submittingLead} data-testid="owner-lead-submit-btn">
                  {submittingLead ? 'Submitting...' : 'Notify Me'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border border-white/10" data-testid="owner-activation-path-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-semibold text-white" data-testid="owner-activation-path-title">Ready to Start Hiring?</h2>
              </div>
              <ul className="space-y-3 text-sm text-muted-foreground mb-5" data-testid="owner-activation-path-list">
                <li>1. Create your free owner account.</li>
                <li>2. Open the owner bartender marketplace.</li>
                <li>3. Save favorites and message your shortlist.</li>
              </ul>
              <div className="flex flex-wrap gap-3" data-testid="owner-activation-path-ctas">
                <Button asChild data-testid="owner-activation-register-btn">
                  <Link to="/register">Create Owner Account</Link>
                </Button>
                <Button asChild variant="outline" data-testid="owner-activation-login-btn">
                  <Link to="/login">Owner Login</Link>
                </Button>
                <Button asChild variant="outline" data-testid="owner-activation-browse-owner-route-btn">
                  <Link to="/owner/browse-bartenders">Open Owner Marketplace</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
